import React, { useState, useRef, useEffect } from 'react';
import { FaPaperPlane } from 'react-icons/fa';
import { usePhaserKeyboardSuspend } from '../../hooks/usePhaserKeyboardSuspend';
import { CHAT_MESSAGE_MAX_LENGTH } from '@shared/schemas/chat.schema.js';

interface MessageInputProps {
  onSendMessage: (content: string) => void;
  placeholder?: string;
}

export const MessageInput: React.FC<MessageInputProps> = ({ 
  onSendMessage, 
  placeholder = 'Type a message...' 
}) => {
  const [message, setMessage] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const phaserKeyboard = usePhaserKeyboardSuspend();
  const isTooLong = message.length > CHAT_MESSAGE_MAX_LENGTH;

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Set height to scrollHeight, but limit max height
      const maxHeight = 120; // ~6 lines
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [message]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Enter submits too, not just the (disabled) button, so the limit is
    // checked here: the server rejects an over-long message
    if (message.trim() && !isComposing && !isTooLong) {
      onSendMessage(message);
      setMessage('');
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Allow new line with Shift+Enter
        return;
      } else {
        // Send message with Enter
        e.preventDefault();
        if (!isComposing) {
          handleSubmit(e);
        }
      }
    }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="p-4">
      <div className="flex items-end gap-3">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onFocus={phaserKeyboard.onFocus}
            onBlur={phaserKeyboard.onBlur}
            placeholder={placeholder}
            className="w-full min-h-[40px] max-h-[120px] px-3 py-2 pr-12 
                     bg-game-cream border border-game-medium/50 rounded-md 
                     text-gray-900 placeholder-gray-600 resize-none
                     focus:outline-none focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20
                     scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent"
            style={{ 
              height: 'auto',
              lineHeight: '1.5'
            }}
          />
          
          {/* Character count for long messages */}
          {message.length > 1500 && (
            <div className={`absolute bottom-1 right-12 text-xs ${
              isTooLong ? 'text-game-orange' : 'text-gray-500'
            }`}>
              {message.length}/{CHAT_MESSAGE_MAX_LENGTH}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={!message.trim() || isComposing || isTooLong}
          className="flex-shrink-0 w-10 h-10 flex items-center justify-center
                   bg-game-accent hover:bg-game-accent/80 disabled:bg-gray-600 disabled:cursor-not-allowed
                   text-white rounded-md transition-colors"
          title="Send message (Enter)"
        >
          <FaPaperPlane className="text-sm" />
        </button>
      </div>

      {/* Help text */}
      <div className="mt-2 text-xs text-gray-300">
        <span className="font-medium">Enter</span> to send • <span className="font-medium">Shift + Enter</span> for new line
      </div>
    </form>
  );
};